import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { FormService, TimeService, ErrorTranslationService } from '@bayclubs/utils';
import { BayclubsAlertService } from '@bayclubs/alert';
import { TextMaskService } from '@bayclubs/ui-text-mask';

import { campsenum } from '../../../../../../models/enums.generated';
import { camps } from '../../../../../../models/models.generated';
import { DiscountManagementApiService } from '../../../../../../services/api/discount-management-api.service';
import { CampsEnumTranslationService } from '../../../../../../services/camps-enum-translation.service';
import { CampsDetailsFormStateService } from '../../../camp-details-form-state-service';

@Component({
    selector: 'app-discount-details-edit',
    templateUrl: './discount-details-edit.component.html',
    styleUrls: ['./discount-details-edit.component.scss'],
    standalone: false
})
export class DiscountDetailsEditComponent implements OnInit {
    @Input() public form: UntypedFormGroup;

    @Output() canceled: EventEmitter<UntypedFormGroup> = new EventEmitter();

    @Input() context: camps.CampDefinitionCreationContext;

    constructor(
        private state: CampsDetailsFormStateService,
        private enumTranslation: CampsEnumTranslationService,
        private translate: TranslateService,
        private api: DiscountManagementApiService,
        private alert: BayclubsAlertService,
        private time: TimeService,
        public mask: TextMaskService,
        private errorTranslate: ErrorTranslationService
    ) {}

    ngOnInit(): void {
        this.clearConditionalValidations();
        this.setConditionalValidations();
    }

    public save() {
        var valPercent = this.form.get('valuePercent').value;

        if (valPercent) this.form.get('valuePercent').setValue(valPercent.toString().replace('%', ''));

        var request = <camps.DiscountSaveRequest>{
            discountId: this.form.get('id').value,
            dateFrom: this.time.toBcDateTime(this.form.get('dateFrom').value),
            dateTo: this.time.toBcDateTime(this.time.endOfDay(this.form.get('dateTo').value)),
            daysFrom: this.form.get('daysFrom').value,
            daysTo: this.form.get('daysTo').value,
            isGlobal: this.form.get('isGlobal').value,
            tier: this.form.get('membershipTier').value,
            minimumNumberOfCamps: this.form.get('minimumNumberOfCamps').value,
            maximumNumberOfCamps: this.form.get('maximumNumberOfCamps').value,
            minimumNumberOfDays: this.form.get('minimumNumberOfDays').value,
            maximumNumberOfDays: this.form.get('maximumNumberOfDays').value,
            name: this.form.get('name').value,
            type: this.form.get('type').value,
            valuePercent: this.form.get('valuePercent').value,
            campId: this.form.get('campId').value,
            applicationOrder: this.form.get('applicationOrder').value
        };

        this.api.save(request).subscribe(x => {
            if (x.isSuccess) {
                this.alert.showOperationSuccess();
                this.form.get('id').setValue(x.value.id);
                this.state.discountChanged.emit(request.discountId);
                this.state.finishEdit(this.form);
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    public cancel() {
        this.canceled.emit(this.form);
    }

    public changeType(value: campsenum.DiscountType) {
        this.form.controls['type'].setValue(value);

        this.clearConditionalValidations();
        this.setConditionalValidations();

        this.clearValues();
    }

    public translateType(discount: campsenum.DiscountType) {
        return this.enumTranslation.discountType(discount);
    }

    public changeTier(value: campsenum.MembershipTier) {
        this.form.controls['membershipTier'].setValue(value);
    }

    public translateTier(tier: campsenum.MembershipTier) {
        return this.enumTranslation.membershipTier(tier);
    }

    public get discountType() {
        return this.form.controls['type'].value;
    }

    public get isSiblings() {
        return this.discountType == campsenum.DiscountType.Siblings;
    }

    public get isEarlyBird() {
        return this.discountType == campsenum.DiscountType.EarlyBird;
    }

    public get isTier() {
        return this.discountType == campsenum.DiscountType.MembershipTier;
    }

    public get isNumberOfDays() {
        return this.discountType == campsenum.DiscountType.NumberOfDays;
    }

    public get isEnrollmentDays() {
        return this.discountType == campsenum.DiscountType.EnrollmentDays;
    }

    public get isPif() {
        return this.discountType == campsenum.DiscountType.PayInFull;
    }

    public get isCartLevel(): boolean {
        return this.form.controls.isCartLevel.value == true;
    }

    public isFormValid() {
        return FormService.isFormValid(this.form);
    }

    public getDiscountTypes() {
        var isCartLevel = this.form.get('isCartLevel').value;

        if (isCartLevel === true) return this.context.cartLevelDiscounts;

        var isPricingLevel = this.form.get('isPricingLevel').value;
        if (isPricingLevel === true) return this.context.pricingLevelDiscounts;

        return [];
    }

    private clearConditionalValidations() {
        this.form.get('minimumNumberOfDays').clearValidators();
        this.form.get('minimumNumberOfDays').updateValueAndValidity();

        this.form.get('dateFrom').clearValidators();
        this.form.get('dateFrom').updateValueAndValidity();

        this.form.get('dateTo').clearValidators();
        this.form.get('dateTo').updateValueAndValidity();

        this.form.get('daysFrom').clearValidators();
        this.form.get('daysFrom').updateValueAndValidity();

        this.form.get('daysTo').clearValidators();
        this.form.get('daysTo').updateValueAndValidity();

        this.form.get('minimumNumberOfCamps').clearValidators();
        this.form.get('minimumNumberOfCamps').updateValueAndValidity();
    }

    private clearValues() {
        this.form.get('minimumNumberOfDays').setValue(null);
        this.form.get('dateFrom').setValue(null);
        this.form.get('dateTo').setValue(null);
        this.form.get('daysFrom').setValue(null);
        this.form.get('daysTo').setValue(null);
        this.form.get('minimumNumberOfCamps').setValue(null);
    }

    private setConditionalValidations() {
 
        if (this.isEarlyBird || this.isEnrollmentDays) {
            this.form.get('dateFrom').setValidators([Validators.required]);
            this.form.get('dateFrom').updateValueAndValidity();

            this.form.get('dateTo').setValidators([Validators.required]);
            this.form.get('dateTo').updateValueAndValidity();
        }

        if (this.isSiblings) {
            this.form.get('minimumNumberOfDays').setValidators([Validators.required, Validators.min(0)]);
            this.form.get('minimumNumberOfDays').updateValueAndValidity();
        }

        if (this.isPif) {
            this.form.get('minimumNumberOfCamps').setValidators([Validators.required, Validators.min(0)]);
            this.form.get('minimumNumberOfCamps').updateValueAndValidity();
        }

        if (this.isNumberOfDays) {
            this.form.get('daysFrom').setValidators([Validators.required, Validators.min(0)]);
            this.form.get('daysTo').setValidators([Validators.required, Validators.min(0)]);
            this.form.get('daysFrom').updateValueAndValidity();
            this.form.get('daysTo').updateValueAndValidity();
        }
    }

    public discountDetailsLabel() {
        return this.translate.instant('CampDetails.DiscountDetails' + campsenum.DiscountType[this.discountType]);
    }
}
